/**
 * AdditionalDocsPanel — the "Additional Documents" card inside DocumentUpload.
 *
 * Lets the customer attach any extra files tagged as 'other'.
 */
import { Plus, FileText, Loader2, CheckCircle2 } from 'lucide-react';
import { Input, toast } from '@finmin/ui';
import { validateDocumentFile, formatFileSize } from '../utils/file-utils';
import { ACCEPT_ATTR } from '../constants';
import type { SlotState } from './doc-upload-slot';
import { PALETTE_FOREST, PALETTE_MINT } from '../tokens';

interface StagedFile {
  id:           string;
  documentType: string;
  file:         File;
  state:        SlotState;
  progress:     number;
  label?:       string;
}

interface AdditionalDocsPanelProps {
  /** Only the 'other' entries from stagedFiles */
  stagedOther:  StagedFile[];
  onStageFile:  (file: File, documentType: string) => void;
  onRemove:     (id: string) => void;
  /** Update the user-supplied display name for a staged 'other' file (required before submit). */
  onLabelChange: (id: string, label: string) => void;
}

export function AdditionalDocsPanel({
  stagedOther,
  onStageFile,
  onRemove,
  onLabelChange,
}: AdditionalDocsPanelProps) {
  return (
    <div className="rounded-2xl border border-border bg-background p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-foreground">Additional Documents</h3>
        <label className="cursor-pointer">
          <input
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            className="sr-only"
            onChange={e => {
              Array.from(e.target.files ?? []).forEach(f => {
                const err = validateDocumentFile(f);
                if (err) { toast.error(err); return; }
                onStageFile(f, 'other');
              });
              e.target.value = '';
            }}
          />
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-lg cursor-pointer"
            style={{ backgroundColor: PALETTE_MINT, color: PALETTE_FOREST }}
          >
            <Plus className="h-3 w-3" /> Add Files
          </span>
        </label>
      </div>

      {stagedOther.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-2">
          No additional files added
        </p>
      ) : (
        <div className="space-y-2">
          {stagedOther.map(sf => {
            const locked  = sf.state === 'uploading' || sf.state === 'uploaded';
            const invalid = !sf.label?.trim() && !locked;
            return (
              <div
                key={sf.id}
                className="rounded-lg border border-border px-3 py-2 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-xs text-foreground truncate flex-1">{sf.file.name}</span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {formatFileSize(sf.file.size)}
                  </span>
                  {sf.state === 'uploading' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" style={{ color: PALETTE_FOREST }} />
                  ) : sf.state === 'uploaded' ? (
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color: PALETTE_FOREST }} />
                  ) : (
                    <button
                      type="button"
                      onClick={() => onRemove(sf.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                      aria-label={`Remove ${sf.file.name}`}
                    >
                      ×
                    </button>
                  )}
                </div>
                <div>
                  <Input
                    value={sf.label ?? ''}
                    onChange={e => onLabelChange(sf.id, e.target.value)}
                    placeholder="Name this document (e.g. Rent agreement)"
                    aria-label={`Name for ${sf.file.name}`}
                    aria-invalid={invalid}
                    disabled={locked}
                    className={`h-8 text-xs ${invalid ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                  />
                  {invalid && (
                    <p className="text-[10px] text-destructive mt-1">A document name is required.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
